import React, { useEffect, useState, useCallback, memo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Users, BarChart3 } from 'lucide-react';
import { useSidebar } from '../../contexts/SidebarContext';
import { fetchUsers, assignDemoPlan } from '../../api/admin';
import MainLayout from '../layout/MainLayout';
import ContentArea from '../layout/ContentArea';
import Sidebar from '../layout/Sidebar';
import PillMenu from '../common/menu/PillMenu';
import Button from '../common/button/Button';
import AdminAnalyticsTab from './AdminAnalyticsTab';

import styles from './styles/AdminContainer.module.css';

const TABS = [
    { id: 'users', label: 'Users', icon: <Users size={16} /> },
    { id: 'analytics', label: 'Analytics', icon: <BarChart3 size={16} /> },
];
const TAB_PATHS = { users: '/admin/users', analytics: '/admin/analytics' };

const AdminContainer = () => {
    const [activeTab, setActiveTab] = useState('users');
    const [userList, setUserList] = useState([]);
    const navigate = useNavigate();
    const location = useLocation();
    const { setPage } = useSidebar();

    const loadUsers = useCallback(async () => {
        try {
            const data = await fetchUsers();
            const sorted =
                [...data.users].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
            setUserList(sorted);
        } catch (e) {
            console.error('Failed to load users', e);
        }
    }, []);

    useEffect(() => {
        document.title = 'JetChats - Admin';
        setPage('admin');
        loadUsers();
    }, [setPage, loadUsers]);

    // Redirect base /admin to /admin/users
    useEffect(() => {
        if (location?.pathname === '/admin' || location?.pathname === '/admin/') {
            navigate('/admin/users', { replace: true });
        }
    }, [location, navigate]);

    // Sync tab with URL
    useEffect(() => {
        if (location?.pathname?.startsWith('/admin/analytics')) {
            setActiveTab('analytics');
        } else if (location?.pathname?.startsWith('/admin/users')) {
            setActiveTab('users');
        }
    }, [location]);

    const handleAssignDemo = useCallback(async (userId, userEmail) => {
        const confirmed = window.confirm(
            `Are you sure you want to give the demo plan to user: ${userEmail}?`
        );

        if (!confirmed) return;

        try {
            await assignDemoPlan(userId);
            loadUsers();
        } catch (e) {
            console.error('Assign demo error', e);
        }
    }, [loadUsers]);

    const handleTabChange = useCallback(tabId => {
        setActiveTab(tabId);
        navigate(TAB_PATHS[tabId]);
    }, [navigate]);

    const formatPlanInfo = useCallback((plan, expiresAt) => {
        if (!expiresAt) return plan;

        const date = new Date(expiresAt);
        const formattedDate = date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });

        return <>{plan}<br />(until {formattedDate})</>;
    }, []);

    const renderUsersTab = useCallback(() => (
        <div className={styles.tableWrapper}>
            <table className={styles.userTable}>
                <thead>
                    <tr>
                        <th>Email</th>
                        <th>Name</th>
                        <th>Admin</th>
                        <th>Registered</th>
                        <th>Plan</th>
                        <th>Tokens</th>
                        <th>Demo Plan</th>
                    </tr>
                </thead>
                <tbody>
                    {userList.map(u => (
                        <tr key={u.id}>
                            <td>{u.email}</td>
                            <td>{u.firstName} {u.lastName}</td>
                            <td>{u.isAdmin ? 'yes' : 'no'}</td>
                            <td>{new Date(u.createdAt).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}</td>
                            <td>{formatPlanInfo(u.plan, u.planExpiresAt)}</td>
                            <td>
                                Subscription: {u.subscriptionTokens}<br />Purchased: {u.purchasedTokens}
                            </td>
                            <td>
                                {u.plan === 'free' && (
                                    <Button
                                        onClick={() => handleAssignDemo(u.id, u.email)}
                                        size="small"
                                        variant="outline"
                                    >
                                        Give Demo Plan
                                    </Button>
                                )}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    ), [userList, handleAssignDemo, formatPlanInfo]);

    return (
        <MainLayout>
            <Sidebar />
            <ContentArea>
                <div className={styles.container}>
                    <div className={styles.content}>
                        <div className={styles.header}>
                            <h1>Admin</h1>
                            <p>Manage users, plans, and view analytics.</p>
                        </div>
                        <div className={styles.tabContainer}>
                            <PillMenu tabs={TABS} activeTab={activeTab} onTabChange={handleTabChange} />
                        </div>
                        {activeTab === 'analytics' ? (
                            <AdminAnalyticsTab />
                        ) : (
                            renderUsersTab()
                        )}
                    </div>
                </div>
            </ContentArea>
        </MainLayout>
    );
};

export default memo(AdminContainer);
